"use client";

import { useEffect, useState } from "react";
import type { z } from "zod";

import { CustomCard } from "@/components/custom-card";
import { cn } from "@/lib/utils";
import type { customCardSchema } from "@/schemas";

import { Carousel, CarouselContent, CarouselItem } from "./index";
import type { CarouselApi } from "./index";

export function CardsCarousel({
  cards,
  isLightText,
}: {
  cards: z.infer<typeof customCardSchema>[];
  isLightText: boolean;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap() + 1);
    };

    setCurrent(api.selectedScrollSnap() + 1);
    api.on("select", onSelect);

    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  return (
    <div className="mx-auto flex w-full max-w-[530px] flex-col gap-4">
      <div className="flex items-center justify-between">
        {cards.map((_, index) => (
          <button
            key={index}
            aria-label={`Slide ${cards[index]?.title ?? index + 1}`}
            onClick={() => api?.scrollTo(index)}
            className={cn(
              "transition-bg bg-grey-100 h-[2px] w-[30%] rounded-[4px] duration-300",
              {
                "bg-grey-900": index + 1 === current,
              }
            )}
          />
        ))}
      </div>
      <Carousel setApi={setApi}>
        <CarouselContent>
          {cards.map((card, index) => (
            <CarouselItem key={index} className="shrink-0 basis-full">
              <CustomCard {...card} isLightText={isLightText} />
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
}
